import type { GameState, Move, Player } from '@qlashgeminiz/qgammon-rules';

/**
 * Ogni giocatore conta i punti dalla PROPRIA casa: il 24 del Bianco e il 24 del
 * Nero sono due punti opposti del tabellone. Il motore invece numera in
 * ASSOLUTO, e l'assoluto coincide con la numerazione del Bianco.
 *
 * MISURATO, non dedotto: `initialPosition('W', false)` mette il Bianco su
 * idx 23/12/7/5 con 2/5/3/5 pedine — cioe' i punti 24/13/8/6 della posizione
 * iniziale standard — e il Nero specchiato su idx 0/11/16/18, che nella sua
 * numerazione sono di nuovo 24/13/8/6.
 */
function pointFor(player: Player, idx: number): number {
  return player === 'W' ? idx + 1 : 24 - idx;
}

/** La sorgente di una mossa: la barra si scrive «bar», un punto col suo numero. */
function fromLabel(player: Player, from: Move['from']): string {
  return from === 'bar' ? 'bar' : String(pointFor(player, from));
}

/** La destinazione: l'uscita si scrive «off», un punto col suo numero. */
function toLabel(player: Player, to: Move['to']): string {
  return to === 'off' ? 'off' : String(pointFor(player, to));
}

/**
 * Un COLPO: la destinazione ospitava ESATTAMENTE UNA pedina avversaria.
 *
 * `points[i]` porta il segno: positivo = pedine del Bianco, negativo = del Nero.
 * Quindi «una sola pedina nemica» e' -1 per il Bianco e +1 per il Nero — e le
 * due condizioni che NON sono un colpo (un punto presidiato da due o piu'
 * nemiche, e una pedina propria) cadono entrambe fuori da questa uguaglianza.
 */
function isHit(player: Player, to: Move['to'], points: readonly number[]): boolean {
  if (to === 'off') return false;
  return points[to] === (player === 'W' ? -1 : 1);
}

export function formatPly(
  moves: readonly Move[],
  player: Player,
  stateBefore: GameState,
): string {
  // Il tabellone cambia FRA una sotto-mossa e l'altra dello stesso turno, e
  // `stateBefore` e' lo stato di prima di TUTTO il turno: se lo rileggessimo a
  // ogni mossa, due pedine mandate sullo stesso punto segnerebbero due colpi
  // dove il blot ne ha subito uno solo. Quindi il punteggio si avanza qui.
  const points = [...stateBefore.points];
  const mine = player === 'W' ? 1 : -1;

  return moves
    .map((m): string => {
      const hit = isHit(player, m.to, points);
      const label = `${fromLabel(player, m.from)}/${toLabel(player, m.to)}${hit ? '*' : ''}`;

      if (typeof m.from === 'number') points[m.from]! -= mine;
      // La colpita se ne va sulla barra: il punto resta con la sola mia pedina.
      if (typeof m.to === 'number') points[m.to] = hit ? mine : points[m.to]! + mine;

      return label;
    })
    .reduce<{ label: string; n: number }[]>((acc, label) => {
      // Le mosse IDENTICHE si raggruppano: «13/11(4)» invece di quattro copie.
      // «Identiche» vuol dire con la STESSA etichetta, asterisco compreso — un
      // colpo e un atterraggio sullo stesso punto restano due voci distinte,
      // perche' dicono due cose diverse a chi legge.
      //
      // E si cercano in TUTTO il turno, non solo fra le adiacenti: il motore
      // produce davvero sequenze alternate come «19/15 24/20 19/15 24/20», e
      // sono proprio i turni piu' larghi (23 caratteri contro 17). Si tiene
      // l'ordine di PRIMA COMPARSA.
      const gia = acc.find((e) => e.label === label);
      if (gia) gia.n += 1;
      else acc.push({ label, n: 1 });
      return acc;
    }, [])
    .map(({ label, n }) => (n > 1 ? `${label}(${n})` : label))
    .join(' ');
}
